import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import {
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  CrownOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, message, Modal, Select, Table, Tag, Tooltip } from "antd";

const { TextArea } = Input;

const ROLE_META = {
  customer:             { label: "Customer",           bg: "#e8f0ff", color: "#1d4ed8", icon: <UserOutlined /> },
  admin:                { label: "Admin",               bg: "#fff7e6", color: "#c2410c", icon: <CrownOutlined /> },
  "super admin":        { label: "Super Admin",         bg: "#ffe4e6", color: "#be123c", icon: <CrownOutlined /> },
  sales:                { label: "Sales",               bg: "#ecfdf5", color: "#047857", icon: <UserOutlined /> },
  deliveries:           { label: "Deliveries",          bg: "#eef2ff", color: "#4338ca", icon: <UserOutlined /> },
  "production manager": { label: "Production Manager",  bg: "#f3e8ff", color: "#7c3aed", icon: <UserOutlined /> },
  "production admin":   { label: "Production Admin",    bg: "#fef3c7", color: "#b45309", icon: <UserOutlined /> },
};

const ROLE_OPTIONS = [
  { value: "all",      label: "All Roles" },
  { value: "customer", label: "Customer" },
  { value: "sales",    label: "Sales" },
  { value: "admin",    label: "Admin" },
];

function getInitials(name) {
  const safeName = String(name || "").trim();
  const parts = safeName.split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[1][0] || "")).toUpperCase();
}

function normalizeUser(user) {
  return {
    ...user,
    id: user?.id ?? user?._id,
    full_name: user?.full_name || user?.name || "",
    email: user?.email || "",
    phone_number: user?.phone_number || "",
    company_name: user?.company_name || "",
    role: user?.role || "customer",
    isApproved: Boolean(user?.isApproved),
    approval_status: user?.approval_status || (user?.isApproved ? "approved" : "pending"),
    createdAt: user?.createdAt || user?.created_at || null,
  };
}

const RequestPage = () => {
  const [users,            setUsers]            = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [search,           setSearch]           = useState("");
  const [roleFilter,       setRoleFilter]       = useState("all");
  const [currentPage,      setCurrentPage]      = useState(1);
  const pageSize = 10;

  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [deleteLoadingId,  setDeleteLoadingId]  = useState(null);
  const [rejectLoadingId,  setRejectLoadingId]  = useState(null);
  const [deleteTarget,     setDeleteTarget]     = useState(null);
  const [rejectTarget,     setRejectTarget]     = useState(null);
  const [rejectReason,     setRejectReason]     = useState("");

  /* Reset filters on every mount (navigation fix) */
  useEffect(() => {
    setSearch("");
    setRoleFilter("all");
    setCurrentPage(1);
  }, []);

  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/admin/users");
      const rawUsers = response.data?.users || [];
      const normalized = rawUsers.map(normalizeUser);
      const pendingUsers = normalized.filter(
        (user) =>
          user.approval_status === "pending" &&
          String(user.role || "").toLowerCase() === "customer"
      );
      pendingUsers.sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      );
      setUsers(pendingUsers);
    } catch (error) {
      console.error(error);
      message.error("Failed to load pending users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPendingUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setCurrentPage(1); }, [search, roleFilter]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch =
        !q ||
        [user.full_name, user.email, user.company_name, user.phone_number, user.role]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      const matchesRole =
        roleFilter === "all" ||
        String(user.role || "").toLowerCase() === roleFilter.toLowerCase();
      return matchesSearch && matchesRole;
    });
  }, [users, search, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  /* ── Actions ── */

  // ✅ Backend: PATCH /api/admin/users/:id/approve  (was PUT — now fixed)
  const approveUser = async (user) => {
    setApproveLoadingId(user.id);
    try {
      await axiosInstance.patch(`/user/${user.id}/approve`);
      message.success(`${user.full_name} approved successfully`);
      await fetchPendingUsers();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to approve user");
    } finally {
      setApproveLoadingId(null);
    }
  };

  // ✅ Backend: PATCH /api/admin/users/:id/reject  (was PUT — now fixed)
  const submitReject = async () => {
    if (!rejectTarget) return;
    setRejectLoadingId(rejectTarget.id);
    try {
      await axiosInstance.patch(`/user/${rejectTarget.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      message.success(`${rejectTarget.full_name} rejected successfully`);
      setRejectTarget(null);
      setRejectReason("");
      await fetchPendingUsers();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to reject user");
    } finally {
      setRejectLoadingId(null);
    }
  };

  // ✅ Backend: DELETE /api/admin/users/:id  (unchanged — DELETE is correct)
  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoadingId(deleteTarget.id);
    try {
      await axiosInstance.delete(`/admin/users/${deleteTarget.id}`);
      message.success(`${deleteTarget.full_name} deleted successfully`);
      setDeleteTarget(null);
      await fetchPendingUsers();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Failed to delete user. This user may still be linked to orders."
      );
    } finally {
      setDeleteLoadingId(null);
    }
  };

  /* ── Table columns ── */
  const columns = [
    {
      title: "ID",
      key: "id",
      width: 64,
      render: (_, __, index) => (
        <span className="text-gray-400 font-mono text-xs">
          #{(currentPage - 1) * pageSize + index + 1}
        </span>
      ),
    },
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
              {getInitials(record.full_name)}
            </div>
            <span className="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-white" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate leading-tight">
              {record.full_name || "—"}
            </p>
            <p className="flex items-center gap-1 text-xs text-gray-400 truncate mt-0.5">
              <MailOutlined className="text-gray-300 flex-shrink-0" />
              {record.email || "—"}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (v) => (
        <span className="text-sm text-gray-700">{v || <span className="text-gray-300">—</span>}</span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone_number",
      key: "phone_number",
      render: (v) => (
        <span className="text-sm text-gray-600">
          {v || <span className="text-gray-300">Not provided</span>}
        </span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => {
        const key = String(role || "").toLowerCase();
        const meta = ROLE_META[key] || {
          label: role || "User",
          bg: "#f3f4f6",
          color: "#374151",
          icon: <UserOutlined />,
        };
        return (
          <Tag
            className="px-2.5 py-0.5 rounded-full text-xs font-semibold border-0 inline-flex items-center gap-1"
            style={{ background: meta.bg, color: meta.color }}
          >
            {meta.icon} {meta.label}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: () => (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          Pending
        </span>
      ),
    },
    {
      title: "Approve",
      key: "approve",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Approve User">
          <Button
            type="primary"
            shape="circle"
            size="small"
            icon={<CheckOutlined />}
            loading={approveLoadingId === record.id}
            onClick={() => approveUser(record)}
            className="!inline-flex !h-8 !w-8 !items-center !justify-center !border-0 !bg-emerald-500 hover:!bg-emerald-600"
          />
        </Tooltip>
      ),
    },
    {
      title: "Reject",
      key: "reject",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Reject User">
          <Button
            type="primary"
            shape="circle"
            size="small"
            icon={<StopOutlined />}
            loading={rejectLoadingId === record.id}
            onClick={() => setRejectTarget(record)}
            className="!inline-flex !h-8 !w-8 !items-center !justify-center !border-0 !bg-orange-500 hover:!bg-orange-600"
          />
        </Tooltip>
      ),
    },
    {
      title: "Delete",
      key: "delete",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Delete User">
          <Button
            danger
            type="primary"
            shape="circle"
            size="small"
            icon={<DeleteOutlined />}
            loading={deleteLoadingId === record.id}
            onClick={() => setDeleteTarget(record)}
            className="!inline-flex !h-8 !w-8 !items-center !justify-center !border-0"
          />
        </Tooltip>
      ),
    },
  ];

  /* ── Render ── */
  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
              <span className="w-9 h-9 bg-white rounded-xl shadow-sm flex items-center justify-center">
                <TeamOutlined className="text-blue-600" />
              </span>
              Approve Users
            </h1>
            <p className="text-sm text-gray-500 mt-1 ml-11">
              Manage and monitor all pending account requests
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-sm border border-gray-100 self-start sm:self-auto">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <span className="text-sm text-gray-500">Pending requests:</span>
            <span className="text-sm font-bold text-gray-900">{users.length}</span>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">

            {/* Search with inline X */}
            <div className="relative flex-1">
              <SearchOutlined
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                style={{ fontSize: 14 }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or company…"
                className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none placeholder-gray-400 text-gray-800 transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors leading-none"
                  title="Clear search"
                >
                  <CloseCircleFilled style={{ fontSize: 15 }} />
                </button>
              )}
            </div>

            {/* Role select — X clears without opening dropdown */}
            <div className="sm:w-48">
              <Select
                value={roleFilter}
                onChange={(val) => setRoleFilter(val)}
                options={ROLE_OPTIONS}
                className="w-full"
                size="middle"
                style={{ height: 42 }}
                suffixIcon={
                  roleFilter !== "all" ? (
                    <CloseCircleFilled
                      style={{ fontSize: 14, color: "#9ca3af", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRoleFilter("all");
                      }}
                    />
                  ) : undefined
                }
              />
            </div>

            {/* Active filter pills */}
            {(search || roleFilter !== "all") && (
              <div className="flex items-center gap-2 flex-wrap">
                {search && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                    "{search.length > 16 ? search.slice(0, 16) + "…" : search}"
                    <button onClick={() => setSearch("")} className="hover:text-blue-900">
                      <CloseOutlined style={{ fontSize: 10 }} />
                    </button>
                  </span>
                )}
                {roleFilter !== "all" && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 text-xs font-medium border border-purple-100">
                    {ROLE_OPTIONS.find((o) => o.value === roleFilter)?.label}
                    <button onClick={() => setRoleFilter("all")} className="hover:text-purple-900">
                      <CloseOutlined style={{ fontSize: 10 }} />
                    </button>
                  </span>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <Table
            columns={columns}
            dataSource={paginatedUsers}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredUsers.length,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
              className: "!px-6 !py-4",
              showTotal: (total, range) =>
                `Showing ${range[0]}–${range[1]} of ${total} requests`,
            }}
            scroll={{ x: 1100 }}
            size="middle"
            locale={{
              emptyText: (
                <div className="py-16 text-center">
                  <TeamOutlined className="text-4xl text-gray-300 mb-3 block" />
                  <p className="text-gray-400 text-sm font-medium">No pending requests found</p>
                  {(search || roleFilter !== "all") && (
                    <button
                      onClick={() => { setSearch(""); setRoleFilter("all"); }}
                      className="mt-2 text-xs text-blue-500 hover:text-blue-700 underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ),
            }}
            rowClassName={() => "hover:bg-gray-50 transition-colors duration-150"}
          />
        </div>

      </div>

      {/* Delete modal */}
      <Modal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        title={
          <div className="flex items-center gap-2.5">
            <ExclamationCircleOutlined className="text-amber-500" />
            <span className="font-semibold">Delete User</span>
          </div>
        }
        footer={[
          <Button key="cancel" onClick={() => setDeleteTarget(null)}>Cancel</Button>,
          <Button
            key="delete" danger type="primary"
            loading={deleteLoadingId === deleteTarget?.id}
            onClick={submitDelete}
          >
            Delete
          </Button>,
        ]}
      >
        {deleteTarget && (
          <div className="space-y-3 pt-1">
            <p className="text-sm text-gray-600">
              This action cannot be undone. Are you sure you want to permanently delete this user?
            </p>
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="font-semibold text-gray-900 text-sm">{deleteTarget.full_name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{deleteTarget.email}</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject modal */}
      <Modal
        open={Boolean(rejectTarget)}
        onCancel={() => { setRejectTarget(null); setRejectReason(""); }}
        title={
          <div className="flex items-center gap-2.5">
            <StopOutlined className="text-orange-500" />
            <span className="font-semibold">Reject Application</span>
          </div>
        }
        footer={[
          <Button key="cancel" onClick={() => { setRejectTarget(null); setRejectReason(""); }}>
            Cancel
          </Button>,
          <Button
            key="reject" danger type="primary"
            loading={rejectLoadingId === rejectTarget?.id}
            onClick={submitReject}
          >
            Reject
          </Button>,
        ]}
        width={560}
      >
        {rejectTarget && (
          <div className="space-y-4 pt-1">
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="font-semibold text-gray-900 text-sm">{rejectTarget.full_name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{rejectTarget.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Rejection reason{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <TextArea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                placeholder="E.g. Missing company details or invalid trade information."
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RequestPage;